import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    InputAdornment,
    Divider,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Tooltip,
    FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DescriptionIcon from '@mui/icons-material/Description';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { ShDialog } from 'shared/SharedStyles/ShFeedback';
import { ShTextFieldV2, ShButton } from 'shared/SharedStyles/ShInputs';
import { ShPaper } from 'shared/SharedStyles/ShSurfaces';
import { ShAvatar } from 'shared/SharedStyles/ShDataDisplay';
import { searchJobApplicantsByResumeText } from 'store/slices/employer/applicants/applicants-list-slice';
import { getApplicantResume } from 'store/slices/employer/applicants/applicant-profile-slice';
import { updateBookmark, updateBookmarkCandidateStatus } from 'store/slices/employer/applicants/applicant-actions-slice';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { ApplicantStages } from '../ApplicantsConstants';
import { SearchContainer, ResultSnippet, ResultHeader, ResultContent, ResultFooter, AvatarContainer } from './ResumeSearchDialog.styles';
import { getInitialsFromName, stringToColor } from 'shared/utils';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import { fetchApplicantsList, setApplicantsList } from 'store/slices/employer/applicants/applicants-list-slice';
import { ShSwitch } from 'shared/SharedStyles/ShNavigation';

interface ResumeSearchDialogProps {
    open: boolean;
    onClose: () => void;
}

export const ResumeSearchDialog = ({ open, onClose }: ResumeSearchDialogProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { jobId } = useParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [lastExecutedQuery, setLastExecutedQuery] = useState('');
    const [includeIncomplete, setIncludeIncomplete] = useState(false);
    const [showMinCharWarning, setShowMinCharWarning] = useState(false);
    const MIN_SEARCH_CHARS = 3;
    const { searchJobApplicantsByResumeTextResults, searchJobApplicantsByResumeTextStatus } = useAppSelector((state) => state.employer.applicants.applicantsList);
    const { bookmarks } = useAppSelector((state) => state.employer.applicants.applicantActions) as { bookmarks: Record<string, boolean> };
        
    useEffect(() => {
        if (searchJobApplicantsByResumeTextResults) {
            searchJobApplicantsByResumeTextResults.forEach(result => {
                dispatch(updateBookmark({ 
                    applicantId: result.candidateEmployerJobsId.toString(), 
                    isBookmarked: result.isBookmarked 
                }));
            });
        }
    }, [searchJobApplicantsByResumeTextResults, dispatch]);

    const handleSearch = async () => {
        if (!searchQuery.trim() || !jobId) return;
        
        if (searchQuery.trim().length < MIN_SEARCH_CHARS) {
            setShowMinCharWarning(true);
            return;
        }
        
        setShowMinCharWarning(false);
        setLastExecutedQuery(searchQuery);
        try {
            await dispatch(searchJobApplicantsByResumeText({ 
                jobId: parseInt(jobId), 
                searchText: searchQuery,
                includeIncomplete: includeIncomplete
            }));
        } catch (error) {
            console.error('Search failed:', error);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (value.trim().length >= MIN_SEARCH_CHARS) {
            setShowMinCharWarning(false);
        }
    };

    const handleViewProfile = (candidateEmployerJobsId: string) => {
        if (jobId) {
            navigate(`/employer/job/${jobId}/applicant/${candidateEmployerJobsId}/details`);
            onClose();
        }
    };

    const handleViewResume = async (candidateId: number) => {
        try {
            const response = await dispatch(getApplicantResume({ applicantId: candidateId })).unwrap();
            if (response?.url) {
                window.open(response.url, '_blank');
            }
        } catch (error) {
            console.error('Error fetching resume:', error);
        }
    };

    const handleBookmark = (applicantId: string, isBookmarked: boolean) => {
        dispatch(updateBookmark({ applicantId, isBookmarked }));
        dispatch(updateBookmarkCandidateStatus({ candidateEmployerId: parseInt(applicantId) }));
    };

    const handleViewInTable = async () => {
        try {
            const response = await dispatch(fetchApplicantsList({
                jobId: parseInt(jobId ?? ''),
                pgNo: 1,
                pgSize: 1000,
                sortCol: 'created_at',
                sortDir: 'desc',
            })).unwrap();

            const resumeSearchIds = (searchJobApplicantsByResumeTextResults ?? []).map(result => 
                parseInt(result.candidateEmployerJobsId)
            );

            const filteredApplicants = response.data.filter(applicant => 
                resumeSearchIds.includes(applicant.candidateEmployerJobId)
            );

            dispatch(setApplicantsList({
                applicants: filteredApplicants,
                paginationParams: {
                    count: filteredApplicants.length,
                    requestedPageNumber: 1,
                    requestedPageSize: filteredApplicants.length,
                    totalPages: 1
                }
            }));

            onClose();
        } catch (error) {
            console.error('Failed to fetch applicants:', error);
        }
    };

    const getStageLabel = (stageValue: string) => {
        const stage = ApplicantStages.find(s => s.value === stageValue);
        return stage?.label || stageValue;
    };

    return (
        <ShDialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
        >
            <ShPaper variant='outlined' headerSection>
                <DialogTitle>
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Search Resumes</Typography>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Search through all completed applicant resumes to find specific skills, experience, or qualifications
                        </Typography>
                    </Box>
                </DialogTitle>
            </ShPaper>
            <DialogContent>
                <SearchContainer>
                    <ShTextFieldV2
                        fullWidth
                        placeholder={`Search through all resumes... (minimum ${MIN_SEARCH_CHARS} characters)`}
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyPress={handleKeyPress}
                        error={showMinCharWarning}
                        helperText={showMinCharWarning ? `Please enter at least ${MIN_SEARCH_CHARS} characters to search` : ''}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <ShButton
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        disabled={!searchQuery.trim() || searchQuery.trim().length < MIN_SEARCH_CHARS}
                    >
                        Search
                    </ShButton>
                </SearchContainer>

                <Box display="flex" alignItems="center" justifyContent="flex-start" mt={1} mb={2} pl={1.5}>
                    <FormControlLabel
                        control={
                            <ShSwitch
                                checked={includeIncomplete}
                                onChange={(e) => setIncludeIncomplete(e.target.checked)}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2" color="text.secondary" pl={2}>
                                Include incomplete applicants
                            </Typography>
                        }
                    />
                </Box>

                <Divider />

                {/* Search Results */}
                <Box>
                    {searchJobApplicantsByResumeTextStatus === 'pending' ? (
                        <Typography variant="body2" color="text.secondary" align="center" pb={2}>
                            Searching...
                        </Typography>
                    ) : lastExecutedQuery && (searchJobApplicantsByResumeTextResults ?? []).length > 0 ? (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom pb={2} pt={2}>
                                Found {(searchJobApplicantsByResumeTextResults ?? []).length} results for "{lastExecutedQuery}"
                            </Typography>
                            <Stack spacing={2}>
                                {(searchJobApplicantsByResumeTextResults ?? []).map((result) => (
                                    <ShPaper key={result.candidateEmployerJobsId} variant="outlined">
                                        <ResultHeader>
                                            <AvatarContainer>
                                                {(() => {
                                                    const initials = getInitialsFromName(result?.fullName || result?.email, true, true);
                                                    const avatarBgColor = stringToColor(initials);
                                                    return (
                                                        <ShAvatar width={35} height={35} backgroundColor={avatarBgColor} className="avatar" alt={result.fullName} src={`user-avatar-to-be-loaded-here`}>
                                                            <Typography variant="body1">{initials}</Typography>
                                                        </ShAvatar>
                                                    );
                                                })()}
                                                <Box flex={1}>
                                                    <Typography variant="subtitle1" component="div">
                                                        {result.fullName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {result.title} • {getStageLabel(result.stage)}
                                                    </Typography>
                                                </Box>
                                            </AvatarContainer>
                                        </ResultHeader>
                                        <ResultContent>
                                            <ResultSnippet>
                                                <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: result.matchedSnippet }} />
                                            </ResultSnippet>
                                            <ResultFooter>
                                                <Typography variant="caption" color="text.secondary">
                                                    Last updated: {result.lastUpdated}
                                                </Typography>
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title={bookmarks[result.candidateEmployerJobsId] ? "Remove bookmark" : "Add bookmark"} placement="left">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleBookmark(result.candidateEmployerJobsId.toString(), !bookmarks[result.candidateEmployerJobsId])}
                                                        >
                                                            {bookmarks[result.candidateEmployerJobsId] ? (
                                                                <BookmarkIcon color="primary" />
                                                            ) : (
                                                                <BookmarkBorderIcon />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View resume" placement="left">
                                                        <IconButton size="small" onClick={() => handleViewResume(parseInt(result.candidateEmployerJobsId))}>
                                                            <DescriptionIcon color="primary" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <ShButton
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<PersonIcon />}
                                                        onClick={() => handleViewProfile(result.candidateEmployerJobsId)}
                                                    >
                                                        View Profile
                                                    </ShButton>
                                                </Stack>
                                            </ResultFooter>
                                        </ResultContent>
                                    </ShPaper>
                                ))}
                            </Stack>
                        </>
                    ) : lastExecutedQuery && searchJobApplicantsByResumeTextStatus === 'success' ? (
                        <Typography variant="body2" color="text.secondary" align="center" pb={2} pt={2}>
                            No results found for "{lastExecutedQuery}"
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" align="center" pb={2} pt={2}>
                            Enter a search term and click Search to find relevant resumes
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <ShPaper variant='outlined' headerSection>
                <DialogActions>
                    <ShButton size="small" variant="contained" color="error"
                        startIcon={<ExitToAppIcon />}
                        onClick={onClose}>
                        Exit
                    </ShButton>
                    <ShButton variant="contained" size="small"
                        startIcon={<BackupTableIcon />}
                        onClick={handleViewInTable}>
                        View Results in Table
                    </ShButton>
                </DialogActions>
            </ShPaper>
        </ShDialog>
    );
}; 